import { useState, useEffect, useCallback } from 'react';
import * as requestService from '../services/requestService';

/**
 * useRequests hook
 * Manages admin requests list retrieval, pagination, and dynamic read/unread toggle.
 */
export function useRequests() {
  const [requests, setRequests] = useState([]);
  const [page, setPage] = useState(0);
  const [size] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const [isLoading, setIsLoading] = useState(false);
  const [isToggling, setIsToggling] = useState({}); // Track loading state per request ID e.g. { [id]: true }
  const [error, setError] = useState('');

  // Fetch requests
  const fetchRequests = useCallback(async (pageNum = 0) => {
    setIsLoading(true);
    setError('');
    try {
      const response = await requestService.getAllRequests(pageNum, size);
      // Response schema: { data, pages, elements, status, message }
      setRequests(Array.isArray(response?.data) ? response.data : []);
      setTotalPages(response?.pages || 1);
      setTotalElements(response?.elements || 0);
      setPage(pageNum);
    } catch (err) {
      console.error(err);
      setError('So\'rovlarni yuklashda xatolik yuz berdi. Qayta urinib ko\'ring.');
    } finally {
      setIsLoading(false);
    }
  }, [size]);

  useEffect(() => {
    fetchRequests(0);
  }, [fetchRequests]);

  // Toggle read/unread state
  const handleToggleIsRead = useCallback(async (id, currentReadState) => {
    setIsToggling((prev) => ({ ...prev, [id]: true }));
    try {
      const targetReadState = !currentReadState;
      await requestService.toggleIsRead(id, targetReadState);
      
      // Update local state directly for responsive feedback without full refetch
      setRequests((prev) =>
        prev.map((req) => (req.id === id ? { ...req, isRead: targetReadState } : req))
      );
    } catch (err) {
      console.error(err);
      setError('Holatni o\'zgartirishda xatolik yuz berdi.');
    } finally {
      setIsToggling((prev) => ({ ...prev, [id]: false }));
    }
  }, []);

  // Compute stats: unread count
  const unreadCount = requests.filter((r) => !r.isRead).length;

  return {
    requests,
    page,
    totalPages,
    totalElements,
    unreadCount,
    isLoading,
    isToggling,
    error,
    fetchRequests,
    handleToggleIsRead,
    setPage,
  };
}
